import * as React from "react";
import { observer } from "mobx-react-lite";
import { formatStrings } from "../../utils/time";
import { Table } from "../../newcomponents/table/table";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { DateStringColumn } from "../../newcomponents/table/columns/generic/dateString";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { BasePathContext } from "../../reactContexts/basePathContext";
import { NGButton } from "../../newcomponents/button/Button";
import { Loader, Plus } from "react-feather";
import { NGInput } from "../../newcomponents/input/input";
import { useDebounce } from "../table/useDebounce";
import { useInitCommandsContext } from "./commandsContext";

interface Props {
  selfLink: string;
}
const CommandsRaw: React.FC<Props> = ({ selfLink }) => {
  const basePath = React.useContext(BasePathContext);

  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = useDebounce(search, 300);
  React.useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);
  React.useEffect(() => {
    if (debouncedSearch) {
      fetchCommandsQuery(debouncedSearch);
    } else {
      fetchCommandsPage(true);
    }
  }, [debouncedSearch]);

  const {
    commands,
    fetchCommandsPage,
    fetchCommandsQuery,
    commandsError: error,
    commandsNextLink: nextLink,
    commandsLoading: isLoading,
  } = useInitCommandsContext(selfLink);

  return (
    <>
      <Table<any>
        title={"Commands"}
        data={commands}
        tableId={`${selfLink}-commands-update1`}
        headerRenderer={() => {
          return (
            <>
              <NGInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
              <TableHeaderRefreshButton onClick={() => fetchCommandsPage(true)} />
            </>
          );
        }}
        enableFilter={false}
        hideSettings
        isLoading={isLoading}
        columns={[
          {
            id: "id",
            label: "Command Id",
            enableResize: true,
            size: 200,
            cell: ({ row }) => {
              const command = row.original;
              return (
                <div className="flex flex-col justify-center" style={{ height: 56 }}>
                  <InternalLink label={command.id} to={`${basePath}/-commands/${command.id}`} />
                </div>
              );
            },
          },
          { id: "type", label: "Type" },
          { id: "lifecycleStage", label: "Lifecycle Stage" },
          {
            id: "messages",
            label: "Messages",
            cell: ({ row }) => {
              const command = row.original;
              const messages: string[] = command.status?.messages || [];
              return <div> {messages.length > 0 ? `${messages.length} Messages` : "-"}</div>;
            },
          },
          DateStringColumn({ id: "created" }, { format: formatStrings.log }),
        ]}
      />
      {nextLink ? (
        <NGButton
          variant={`secondary`}
          outlined
          className="w-full font-semibold mt-4"
          onClick={() => {
            if (!isLoading) {
              fetchCommandsPage();
            }
          }}
          disabled={isLoading}
        >
          <div className="flex items-center gap-2">
            {isLoading ? <Loader className="animate-spin" /> : <Plus />}
            {isLoading ? <div>Loading More Items</div> : <span>Load More Commands</span>}
          </div>
        </NGButton>
      ) : null}
    </>
  );
};

export const Commands = observer(CommandsRaw);
